import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, MoveUp, MoveDown, LayoutGrid, TableIcon, X } from "lucide-react";
import { getIcon, availableIcons } from "@/lib/iconMapping";

interface FeatureCategory {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  display_order: number;
}

interface Feature {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  icon: string;
  plan: string;
  show_both_badges: boolean;
  display_order: number;
  feature_details?: FeatureDetail[];
}

interface FeatureDetail {
  id: string;
  feature_id: string;
  detail_text: string;
  display_order: number;
}

interface ComparisonFeature {
  id: string;
  name: string;
  basic: boolean;
  standard: boolean;
  display_order: number;
}

export default function FeaturesManagement() {
  const [categories, setCategories] = useState<FeatureCategory[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [comparisonFeatures, setComparisonFeatures] = useState<ComparisonFeature[]>([]);
  const [loading, setLoading] = useState(false);

  // Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FeatureCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "", icon: "Star" });

  // Feature dialog state
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [featureForm, setFeatureForm] = useState({
    category_id: "",
    name: "",
    description: "",
    icon: "Star",
    plan: "basic",
    show_both_badges: false,
    details: [""]
  });

  // Comparison dialog state
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [editingComparison, setEditingComparison] = useState<ComparisonFeature | null>(null);
  const [comparisonForm, setComparisonForm] = useState({ name: "", basic: false, standard: false });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchCategories(), fetchFeatures(), fetchComparisonFeatures()]);
    setLoading(false);
  };

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('feature_categories')
      .select('*')
      .order('display_order');
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setCategories(data || []);
    }
  };

  const fetchFeatures = async () => {
    const { data, error } = await supabase
      .from('features')
      .select(`*, feature_details(*)`)
      .order('display_order');
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setFeatures(data || []);
    }
  };

  const fetchComparisonFeatures = async () => {
    const { data, error } = await supabase
      .from('comparison_features')
      .select('*')
      .order('display_order');
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setComparisonFeatures(data || []);
    }
  };

  // Category handlers
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast({ title: "Error", description: "Category name is required", variant: "destructive" });
      return;
    }

    setLoading(true);
    if (editingCategory) {
      const { error } = await supabase
        .from('feature_categories')
        .update({
          name: categoryForm.name,
          description: categoryForm.description || null,
          icon: categoryForm.icon
        })
        .eq('id', editingCategory.id);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Success", description: "Category updated" });
      }
    } else {
      const maxOrder = Math.max(0, ...categories.map(c => c.display_order));
      const { error } = await supabase
        .from('feature_categories')
        .insert({
          name: categoryForm.name,
          description: categoryForm.description || null,
          icon: categoryForm.icon,
          display_order: maxOrder + 1
        });
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Success", description: "Category created" });
      }
    }
    setCategoryDialogOpen(false);
    setEditingCategory(null);
    setCategoryForm({ name: "", description: "", icon: "Star" });
    fetchCategories();
    setLoading(false);
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm("Delete this category? All features in it will also be deleted.")) return;
    const { error } = await supabase.from('feature_categories').delete().eq('id', id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Category deleted" });
      fetchCategories();
      fetchFeatures();
    }
  };

  const handleMoveCategory = async (id: string, direction: 'up' | 'down') => {
    const index = categories.findIndex(c => c.id === id);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === categories.length - 1)) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const updates = [
      { id: categories[index].id, display_order: categories[swapIndex].display_order },
      { id: categories[swapIndex].id, display_order: categories[index].display_order }
    ];

    for (const update of updates) {
      await supabase.from('feature_categories').update({ display_order: update.display_order }).eq('id', update.id);
    }
    fetchCategories();
  };

  // Feature handlers
  const handleSaveFeature = async () => {
    if (!featureForm.name.trim() || !featureForm.category_id) {
      toast({ title: "Error", description: "Name and category are required", variant: "destructive" });
      return;
    }

    setLoading(true);
    const featureData = {
      category_id: featureForm.category_id,
      name: featureForm.name,
      description: featureForm.description || null,
      icon: featureForm.icon,
      plan: featureForm.plan,
      show_both_badges: featureForm.show_both_badges
    };

    if (editingFeature) {
      const { error } = await supabase.from('features').update(featureData).eq('id', editingFeature.id);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        setLoading(false);
        return;
      }

      // Update details - delete existing and insert new
      await supabase.from('feature_details').delete().eq('feature_id', editingFeature.id);
      const details = featureForm.details.filter(d => d.trim());
      if (details.length > 0) {
        await supabase.from('feature_details').insert(
          details.map((detail, i) => ({
            feature_id: editingFeature.id,
            detail_text: detail,
            display_order: i
          }))
        );
      }
      toast({ title: "Success", description: "Feature updated" });
    } else {
      const categoryFeatures = features.filter(f => f.category_id === featureForm.category_id);
      const maxOrder = Math.max(0, ...categoryFeatures.map(f => f.display_order));
      
      const { data, error } = await supabase
        .from('features')
        .insert({ ...featureData, display_order: maxOrder + 1 })
        .select()
        .single();

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        setLoading(false);
        return;
      }

      // Insert details
      const details = featureForm.details.filter(d => d.trim());
      if (details.length > 0) {
        await supabase.from('feature_details').insert(
          details.map((detail, i) => ({
            feature_id: data.id,
            detail_text: detail,
            display_order: i
          }))
        );
      }
      toast({ title: "Success", description: "Feature created" });
    }

    setFeatureDialogOpen(false);
    setEditingFeature(null);
    setFeatureForm({ category_id: "", name: "", description: "", icon: "Star", plan: "basic", show_both_badges: false, details: [""] });
    fetchFeatures();
    setLoading(false);
  };

  const handleDeleteFeature = async (id: string) => {
    if (!confirm("Delete this feature?")) return;
    const { error } = await supabase.from('features').delete().eq('id', id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Feature deleted" });
      fetchFeatures();
    }
  };

  const handleMoveFeature = async (feature: Feature, direction: 'up' | 'down') => {
    const categoryFeatures = features.filter(f => f.category_id === feature.category_id).sort((a, b) => a.display_order - b.display_order);
    const index = categoryFeatures.findIndex(f => f.id === feature.id);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === categoryFeatures.length - 1)) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const updates = [
      { id: categoryFeatures[index].id, display_order: categoryFeatures[swapIndex].display_order },
      { id: categoryFeatures[swapIndex].id, display_order: categoryFeatures[index].display_order }
    ];

    for (const update of updates) {
      await supabase.from('features').update({ display_order: update.display_order }).eq('id', update.id);
    }
    fetchFeatures();
  };

  // Comparison handlers
  const handleSaveComparison = async () => {
    if (!comparisonForm.name.trim()) {
      toast({ title: "Error", description: "Name is required", variant: "destructive" });
      return;
    }

    setLoading(true);
    if (editingComparison) {
      const { error } = await supabase
        .from('comparison_features')
        .update(comparisonForm)
        .eq('id', editingComparison.id);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Success", description: "Comparison feature updated" });
      }
    } else {
      const maxOrder = Math.max(0, ...comparisonFeatures.map(c => c.display_order));
      const { error } = await supabase
        .from('comparison_features')
        .insert({ ...comparisonForm, display_order: maxOrder + 1 });
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Success", description: "Comparison feature created" });
      }
    }
    setComparisonDialogOpen(false);
    setEditingComparison(null);
    setComparisonForm({ name: "", basic: false, standard: false });
    fetchComparisonFeatures();
    setLoading(false);
  };

  const handleDeleteComparison = async (id: string) => {
    if (!confirm("Delete this comparison feature?")) return;
    const { error } = await supabase.from('comparison_features').delete().eq('id', id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Comparison feature deleted" });
      fetchComparisonFeatures();
    }
  };

  const handleMoveComparison = async (id: string, direction: 'up' | 'down') => {
    const index = comparisonFeatures.findIndex(c => c.id === id);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === comparisonFeatures.length - 1)) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const updates = [
      { id: comparisonFeatures[index].id, display_order: comparisonFeatures[swapIndex].display_order },
      { id: comparisonFeatures[swapIndex].id, display_order: comparisonFeatures[index].display_order }
    ];

    for (const update of updates) {
      await supabase.from('comparison_features').update({ display_order: update.display_order }).eq('id', update.id);
    }
    fetchComparisonFeatures();
  };

  const openEditCategory = (category: FeatureCategory) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, description: category.description || "", icon: category.icon });
    setCategoryDialogOpen(true);
  };

  const openEditFeature = (feature: Feature) => {
    setEditingFeature(feature);
    setFeatureForm({
      category_id: feature.category_id,
      name: feature.name,
      description: feature.description || "",
      icon: feature.icon,
      plan: feature.plan,
      show_both_badges: feature.show_both_badges,
      details: feature.feature_details?.sort((a, b) => a.display_order - b.display_order).map(d => d.detail_text) || [""]
    });
    setFeatureDialogOpen(true);
  };

  const openEditComparison = (comparison: ComparisonFeature) => {
    setEditingComparison(comparison);
    setComparisonForm({ name: comparison.name, basic: comparison.basic, standard: comparison.standard });
    setComparisonDialogOpen(true);
  };

  const addDetailField = () => {
    setFeatureForm(prev => ({ ...prev, details: [...prev.details, ""] }));
  };

  const updateDetail = (index: number, value: string) => {
    setFeatureForm(prev => ({
      ...prev,
      details: prev.details.map((d, i) => i === index ? value : d)
    }));
  };

  const removeDetail = (index: number) => {
    setFeatureForm(prev => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="cards" className="w-full">
        <TabsList>
          <TabsTrigger value="cards">
            <LayoutGrid className="h-4 w-4 mr-2" />
            Feature Cards
          </TabsTrigger>
          <TabsTrigger value="comparison">
            <TableIcon className="h-4 w-4 mr-2" />
            Comparison Table
          </TabsTrigger>
        </TabsList>

        {/* Feature Cards Tab */}
        <TabsContent value="cards" className="space-y-6">
          {/* Categories Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Feature Categories</CardTitle>
                <CardDescription>Organize features into sections</CardDescription>
              </div>
              <Dialog open={categoryDialogOpen} onOpenChange={(open) => {
                setCategoryDialogOpen(open);
                if (!open) {
                  setEditingCategory(null);
                  setCategoryForm({ name: "", description: "", icon: "Star" });
                }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm"><Plus className="h-4 w-4 mr-2" />Add Category</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingCategory ? "Edit Category" : "Add Category"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Core Features"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Brief description of this category"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Icon</Label>
                      <Select value={categoryForm.icon} onValueChange={(v) => setCategoryForm(prev => ({ ...prev, icon: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {availableIcons.map(icon => {
                            const IconComponent = getIcon(icon);
                            return (
                              <SelectItem key={icon} value={icon}>
                                <div className="flex items-center gap-2">
                                  <IconComponent className="h-4 w-4" />
                                  {icon}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleSaveCategory} disabled={loading} className="w-full">
                      {loading ? "Saving..." : "Save Category"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {categories.map((category, index) => {
                  const IconComponent = getIcon(category.icon);
                  const categoryFeatures = features.filter(f => f.category_id === category.id);
                  return (
                    <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <IconComponent className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium">{category.name}</p>
                          <p className="text-sm text-muted-foreground">{categoryFeatures.length} features</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleMoveCategory(category.id, 'up')} disabled={index === 0}>
                          <MoveUp className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleMoveCategory(category.id, 'down')} disabled={index === categories.length - 1}>
                          <MoveDown className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => openEditCategory(category)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteCategory(category.id)}>
                          <Trash2 className="h-4 w-4 text-black dark:text-white" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {categories.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No categories yet. Add one to get started.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Features Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Features</CardTitle>
                <CardDescription>Individual features within categories</CardDescription>
              </div>
              <Dialog open={featureDialogOpen} onOpenChange={(open) => {
                setFeatureDialogOpen(open);
                if (!open) {
                  setEditingFeature(null);
                  setFeatureForm({ category_id: "", name: "", description: "", icon: "Star", plan: "basic", show_both_badges: false, details: [""] });
                }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm" disabled={categories.length === 0}>
                    <Plus className="h-4 w-4 mr-2" />Add Feature
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingFeature ? "Edit Feature" : "Add Feature"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category</Label>
                        <Select value={featureForm.category_id} onValueChange={(v) => setFeatureForm(prev => ({ ...prev, category_id: v }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Plan</Label>
                        <Select value={featureForm.plan} onValueChange={(v) => setFeatureForm(prev => ({ ...prev, plan: v }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="standard">Standard</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={featureForm.name}
                        onChange={(e) => setFeatureForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Main Dashboard"
                      />
                    </div>
                    <div>
                      <Label>Short Description</Label>
                      <Textarea
                        value={featureForm.description}
                        onChange={(e) => setFeatureForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Brief description of this feature"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Icon</Label>
                      <Select value={featureForm.icon} onValueChange={(v) => setFeatureForm(prev => ({ ...prev, icon: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {availableIcons.map(icon => {
                            const IconComponent = getIcon(icon);
                            return (
                              <SelectItem key={icon} value={icon}>
                                <div className="flex items-center gap-2">
                                  <IconComponent className="h-4 w-4" />
                                  {icon}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={featureForm.show_both_badges}
                        onCheckedChange={(checked) => setFeatureForm(prev => ({ ...prev, show_both_badges: checked }))}
                      />
                      <Label>Show both plan badges (for features in both plans)</Label>
                    </div>
                    <div>
                      <Label>Detailed Bullet Points</Label>
                      <div className="space-y-2 mt-2">
                        {featureForm.details.map((detail, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              value={detail}
                              onChange={(e) => updateDetail(index, e.target.value)}
                              placeholder={`Detail ${index + 1}`}
                            />
                            {featureForm.details.length > 1 && (
                              <Button size="icon" variant="ghost" onClick={() => removeDetail(index)}>
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={addDetailField}>
                          <Plus className="h-4 w-4 mr-2" />Add Detail
                        </Button>
                      </div>
                    </div>
                    <Button onClick={handleSaveFeature} disabled={loading} className="w-full">
                      {loading ? "Saving..." : "Save Feature"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {categories.map(category => {
                  const categoryFeatures = features.filter(f => f.category_id === category.id).sort((a, b) => a.display_order - b.display_order);
                  if (categoryFeatures.length === 0) return null;
                  
                  return (
                    <div key={category.id} className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">{category.name}</h4>
                      {categoryFeatures.map((feature, index) => {
                        const IconComponent = getIcon(feature.icon);
                        return (
                          <div key={feature.id} className="flex items-center justify-between p-3 border rounded-lg ml-4">
                            <div className="flex items-center gap-3">
                              <IconComponent className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{feature.name}</p>
                                  <Badge variant="outline" className={feature.plan === 'basic' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                                    {feature.plan}
                                  </Badge>
                                  {feature.show_both_badges && <Badge variant="secondary">Both</Badge>}
                                </div>
                                <p className="text-sm text-muted-foreground">{feature.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button size="icon" variant="ghost" onClick={() => handleMoveFeature(feature, 'up')} disabled={index === 0}>
                                <MoveUp className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => handleMoveFeature(feature, 'down')} disabled={index === categoryFeatures.length - 1}>
                                <MoveDown className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => openEditFeature(feature)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => handleDeleteFeature(feature.id)}>
                                <Trash2 className="h-4 w-4 text-black dark:text-white" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {features.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No features yet. Add categories first, then add features.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comparison Table Tab */}
        <TabsContent value="comparison" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Comparison Table Features</CardTitle>
                <CardDescription>Features shown in the plan comparison table</CardDescription>
              </div>
              <Dialog open={comparisonDialogOpen} onOpenChange={(open) => {
                setComparisonDialogOpen(open);
                if (!open) {
                  setEditingComparison(null);
                  setComparisonForm({ name: "", basic: false, standard: false });
                }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm"><Plus className="h-4 w-4 mr-2" />Add Comparison Feature</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingComparison ? "Edit Comparison Feature" : "Add Comparison Feature"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Feature Name</Label>
                      <Input
                        value={comparisonForm.name}
                        onChange={(e) => setComparisonForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Main Dashboard"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={comparisonForm.basic}
                          onCheckedChange={(checked) => setComparisonForm(prev => ({ ...prev, basic: checked }))}
                        />
                        <Label>Basic Plan</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={comparisonForm.standard}
                          onCheckedChange={(checked) => setComparisonForm(prev => ({ ...prev, standard: checked }))}
                        />
                        <Label>Standard Plan</Label>
                      </div>
                    </div>
                    <Button onClick={handleSaveComparison} disabled={loading} className="w-full">
                      {loading ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead className="text-center w-24">Basic</TableHead>
                    <TableHead className="text-center w-24">Standard</TableHead>
                    <TableHead className="w-40">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonFeatures.map((feature, index) => (
                    <TableRow key={feature.id}>
                      <TableCell className="font-medium">{feature.name}</TableCell>
                      <TableCell className="text-center">
                        {feature.basic ? <Badge className="bg-green-100 text-green-700">✓</Badge> : <Badge variant="outline">✗</Badge>}
                      </TableCell>
                      <TableCell className="text-center">
                        {feature.standard ? <Badge className="bg-green-100 text-green-700">✓</Badge> : <Badge variant="outline">✗</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" onClick={() => handleMoveComparison(feature.id, 'up')} disabled={index === 0}>
                            <MoveUp className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleMoveComparison(feature.id, 'down')} disabled={index === comparisonFeatures.length - 1}>
                            <MoveDown className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => openEditComparison(feature)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDeleteComparison(feature.id)}>
                            <Trash2 className="h-4 w-4 text-black dark:text-white" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {comparisonFeatures.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No comparison features yet. Add one to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
